import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import tagsService from '../../services/tagsService';
import iconEdit from '../../assets/icon-edit-thin.svg';
import TooltipAction from '../TooltipAction/TooltipAction';
import './TalentTagCard.css';

const TalentTagCard = ({ talentId, talentTags = [], onTagsUpdate, onFlipBack }) => {
  const [companyTags, setCompanyTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localTags, setLocalTags] = useState(talentTags || []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tagColors, setTagColors] = useState([]);
  const [formData, setFormData] = useState({ name: '', colorHex: '' });
  const [editingTag, setEditingTag] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, message: '', position: null });

  useEffect(() => {
    setLocalTags(talentTags || []);
  }, [talentTags]);

  useEffect(() => {
    loadCompanyTags();
    loadTagColors();
  }, []);

  const loadCompanyTags = async () => {
    try {
      setLoading(true);
      const response = await tagsService.getCompanyTags(false);
      setCompanyTags(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      toast.error('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  };

  const loadTagColors = async () => {
    try {
      const response = await tagsService.getTagColors();
      setTagColors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar cores:', error);
    }
  };

  const handleAddTag = async (tag) => {
    try {
      await tagsService.addTagToTalent(talentId, tag.id);
      setLocalTags((prev) => {
        const newTags = [...prev, tag];
        if (onTagsUpdate) {
          onTagsUpdate(newTags);
        }
        return newTags;
      });
    } catch (error) {
      console.error('Erro ao adicionar tag:', error);
      const errorMessage = error.response?.data?.errors?.[0]?.title || 'Erro ao adicionar tag';
      toast.error(errorMessage);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await tagsService.removeTagFromTalent(talentId, tagId);
      setLocalTags((prev) => {
        const newTags = prev.filter((t) => t.id !== tagId);
        if (onTagsUpdate) {
          onTagsUpdate(newTags);
        }
        return newTags;
      });
    } catch (error) {
      console.error('Erro ao remover tag:', error);
      toast.error('Erro ao remover tag');
    }
  };

  const isTagAssigned = (tagId) => {
    return localTags.some((t) => t.id === tagId);
  };

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      colorHex: tagColors?.[0]?.colorHex || '#EC5569',
    });
    setShowCreateModal(true);
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name || '',
      colorHex: tag.colorHex,
    });
    setShowCreateModal(true);
  };

  useEffect(() => {
    const handleGlobalTagUpdate = (e) => {
      const updated = e.detail;
      if (updated && updated.id) {
        setCompanyTags(prev => prev.map(tag => tag.id === updated.id ? { ...tag, ...updated } : tag));
      }
    };
    window.addEventListener('tag-updated', handleGlobalTagUpdate);
    return () => window.removeEventListener('tag-updated', handleGlobalTagUpdate);
  }, []);

  const handleSaveTag = async () => {
    try {
      let savedTag = null;

      if (editingTag) {
        // Edit existing tag
        await tagsService.updateCompanyTag(
          editingTag.id,
          formData.name ? formData.name.trim() : '',
          formData.colorHex
        );

        // Update tags list UI to reflect new name/color
        const updatedTag = { ...editingTag, name: formData.name, colorHex: formData.colorHex };
        setCompanyTags(prev => prev.map(t => t.id === editingTag.id ? updatedTag : t));
        savedTag = updatedTag;

        // Dispatch global event so other cards update immediately
        window.dispatchEvent(new CustomEvent('tag-updated', { detail: updatedTag }));

      } else {
        // Create new tag
        const response = await tagsService.createCompanyTag(
          formData.name ? formData.name.trim() : '',
          formData.colorHex
        );
        const created = response?.data;
        if (created) {
          setCompanyTags((prev) => [...prev, created]);
          savedTag = created;
        } else {
          await loadCompanyTags();
          if (!created) {
            setShowCreateModal(false);
            setEditingTag(null);
            return;
          }
        }
      }

      // Auto-assign to talent if valid tag and not already assigned
      if (savedTag) {
        // We use functional update to ensure we check against LATEST assignments
        setLocalTags(currentLocalTags => {
          const isAssigned = currentLocalTags.some(t => t.id === savedTag.id);

          // If assigned, we just need to update the display (color/name)
          if (isAssigned) {
            const newTags = currentLocalTags.map(t => t.id === savedTag.id ? savedTag : t);
            if (onTagsUpdate) onTagsUpdate(newTags);
            return newTags;
          }

          return currentLocalTags;
        });

        const isAssignedNow = localTags.some(t => t.id === savedTag.id);

        if (!isAssignedNow) {
          // Perform API call
          try {
            await tagsService.addTagToTalent(talentId, savedTag.id);
            // Now update state to include it
            setLocalTags(prev => {
              if (prev.some(t => t.id === savedTag.id)) return prev; // race check
              const newTags = [...prev, savedTag];
              if (onTagsUpdate) onTagsUpdate(newTags);
              return newTags;
            });
          } catch (err) {
            console.error('Auto-assign failed', err);
          }
        } else {
          // If it WAS assigned, we still need to update the local state with new colors/name (editing case)
          setLocalTags(prev => {
            const exists = prev.some(t => t.id === savedTag.id);
            if (exists) {
              // Just update data
              const newTags = prev.map(t => t.id === savedTag.id ? savedTag : t);
              if (onTagsUpdate) onTagsUpdate(newTags);
              return newTags;
            }
            return prev;
          });
        }
      }

      setShowCreateModal(false);
      setEditingTag(null);
      setFormData({ name: '', colorHex: '' });

    } catch (error) {
      console.error('Erro ao salvar tag:', error);
      const msg = error.response?.data?.errors?.[0]?.title || 'Erro ao salvar tag';
      toast.error(msg);
    }
  };

  const handleDeleteTag = async () => {
    if (!editingTag || !window.confirm('Arquivar esta tag?')) return;
    try {
      await tagsService.archiveTag(editingTag.id);

      setCompanyTags(prev => prev.filter(t => t.id !== editingTag.id));

      // Update local assignments
      setLocalTags((prev) => {
        const newTags = prev.filter((t) => t.id !== editingTag.id);
        if (onTagsUpdate) onTagsUpdate(newTags);
        return newTags;
      });

      setShowCreateModal(false);
      setEditingTag(null);
    } catch (error) {
      console.error('Erro ao arquivar tag:', error);
      toast.error('Erro ao arquivar tag');
    }
  };

  const renderModal = () => (
    <div className="talent-tag-card-modal-overlay" onClick={() => setShowCreateModal(false)}>
      <div className="talent-tag-card-modal" onClick={(e) => e.stopPropagation()}>
        <div className="talent-tag-card-modal-header">
          <h3>{editingTag ? 'Editar Tag' : 'Nova Tag'}</h3>
          <button onClick={() => setShowCreateModal(false)}>✕</button>
        </div>
        <div className="talent-tag-card-modal-body">
          <label>Nome da Tag (opcional)</label>
          <input
            type="text"
            value={formData.name}
            maxLength={50}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Digite o nome"
          />
          <label>Cor</label>
          <div className="talent-tag-card-colors">
            {tagColors.map((colorObj) => (
              <div
                key={colorObj.colorHex}
                className={`talent-tag-card-color ${formData.colorHex === colorObj.colorHex ? 'selected' : ''}`}
                style={{ backgroundColor: colorObj.colorHex }}
                onClick={() => setFormData({ ...formData, colorHex: colorObj.colorHex })}
              />
            ))}
          </div>
        </div>
        <div className="talent-tag-card-modal-footer">
          {editingTag && (
            <button
              className="btnDanger"
              onClick={handleDeleteTag}
              style={{ marginRight: 'auto', borderColor: '#ff4d4f', color: '#ff4d4f' }}
            >
              Arquivar
            </button>
          )}
          <button onClick={() => setShowCreateModal(false)}>Cancelar</button>
          <button onClick={handleSaveTag} className="primary">
            {editingTag ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="talent-tag-card">
      <div className="talent-tag-card-header">
        <div className="talent-tag-card-title">Etiquetas</div>
        <button
          className="talent-tag-card-close"
          onClick={onFlipBack}
          aria-label="Voltar"
        >
          ✕
        </button>
      </div>

      <div className="talent-tag-card-content">
        {loading ? (
          <div className="talent-tag-card-loading">Carregando...</div>
        ) : (
          <>
            {companyTags.length === 0 ? (
              <div className="talent-tag-card-empty">
                Nenhuma tag disponível
              </div>
            ) : (
              <div className="talent-tag-card-list">
                {companyTags.map((tag) => {
                  const assigned = isTagAssigned(tag.id);
                  return (
                    <div
                      key={tag.id}
                      className="talent-tag-card-row"
                    >
                      <input
                        type="checkbox"
                        checked={assigned}
                        onChange={() => {
                          if (assigned) {
                            handleRemoveTag(tag.id);
                          } else {
                            handleAddTag(tag);
                          }
                        }}
                        className="talent-tag-card-checkbox"
                      />
                      <div
                        className="talent-tag-card-tag-bar"
                        style={{ backgroundColor: tag.colorHex }}
                        title=""
                        onClick={() => {
                          if (assigned) {
                            handleRemoveTag(tag.id);
                          } else {
                            handleAddTag(tag);
                          }
                        }}
                        onMouseEnter={(e) => {
                          const element = e.currentTarget;
                          if (element.scrollWidth > element.clientWidth) {
                            const rect = element.getBoundingClientRect();
                            setTooltip({
                              visible: true,
                              message: tag.name,
                              position: {
                                top: rect.top + rect.height / 2,
                                left: rect.left + rect.width + 8,
                              },
                            });
                          }
                        }}
                        onMouseLeave={() => setTooltip({ visible: false, message: '', position: null })}
                      >
                        {tag.name}
                      </div>
                      <button
                        className="talent-tag-card-edit-icon"
                        onClick={() => openEditModal(tag)}
                      >
                        <img src={iconEdit} alt="Editar" style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="talent-tag-card-footer">
        <button
          className="talent-tag-card-create-button"
          onClick={openCreateModal}
        >
          Criar Tag
        </button>
      </div>

      {showCreateModal && ReactDOM.createPortal(renderModal(), document.body)}

      {tooltip.visible && tooltip.position && ReactDOM.createPortal(
        <TooltipAction
          message={tooltip.message}
          visible={tooltip.visible}
          style={{
            position: 'fixed',
            top: `${tooltip.position.top}px`,
            left: `${tooltip.position.left}px`,
            transform: 'translateY(-50%)',
            zIndex: 2147483647,
          }}
        />,
        document.body
      )}
    </div>
  );
};

export default TalentTagCard;
